<template lang="pug">
  v-container.px-10(fluid)
    v-progress-linear(
      :active="loading",
      indeterminate,
      color="white",
      absolute,
      top
    )
    v-row.text-center(v-if="!entity")
      v-col.py-0
        h2.primary--text {{$t('Common.noEntitySelected')}}
        small.white--text {{$t('Common.selectEntityHelp')}}
    v-row(v-else)
      v-col.py-0
        v-card.px-10.py-5.my-5
          v-overlay(absolute, v-if="loading")
          g-data-table(
            dense,
            :headers="headers",
            :items="paginationFiltered",
            :options.sync="options",
            :server-items-length="pagination.total",
            v-bind="{ loading }",
          )
            template(v-slot:no-data)
              h2 {{ $pascalToHuman(entityName) }} doesn't have items
              small Create an item for this entity or change search criteria.
            template(v-slot:top)
              v-row.table__top
                v-col.py-0
                  app-entity-search(
                    :criteria="$getInitialCriterias($route.params.criteria, pagination.where)",
                    :wheres="$route.params.hiddenCriteria ? [$route.params.hiddenCriteria] : []",
                    @pagination="pagination = $event",
                    :loads="loads",
                    :loadExtraDatas="$getExtraDataForIndex($store, 'Shipment')",
                    :entity="entity",
                    :rowsPerPage="options.itemsPerPage || null"
                  )
                v-col.pt-4.text-right(v-if="ShowEntityCreate(entityName)" cols="auto")
                  v-tooltip(left)
                    template(v-slot:activator="{ on }")
                      g-btn.h8(
                        color="success",
                        v-on="on",
                        link,
                        @click.native="toCreate()",
                      ) {{$t('Common.createNew')}}
                    span {{ $t('Common.addEntity', { entity: $tc('Shipment._name') }) }}
                v-col.pt-4(v-if="!portal", cols="auto")
                  v-dialog(v-model="dialog", persistent, max-width="600px")
                    template(v-slot:activator="{ on: dialogModal }")
                      v-tooltip(left)
                        template(v-slot:activator="{ on: tooltip }")
                          v-btn.px-0(
                            color="primary",
                            style="min-width: 40px;",
                            v-on="{ ...dialogModal, ...tooltip }"
                          )
                            v-icon mdi-cog-outline
                        span {{ $t('Common.filterColumns') }}
                    field-arrangement(
                      @close="dialog=false",
                      @selected="setHeaders",
                      :entity="entity",
                      :columns="headers",
                    )
            template(v-if="!portal" v-slot:item.status.name="{ item }")
              v-select(
                :label="(item.status||{}).name",
                :value="item.status",
                :items="statusList",
                item-text="name",
                item-value="externalId",
                :disabled="(item.status||{}).blockEdit",
                @input="changeStatus($event, item)",
              )
            template(v-slot:item.assignee.fullName="{ item }")
              v-select(
                :label="(item.assignee||{}).fullName",
                :value="(item.assignee||{}).id",
                :items="assignedList",
                item-text="fullName",
                item-value="id",
                return-object,
                :disabled="!(item.assignee==null)",
                @input="changeAssigned($event, item)",
              )
            template(v-slot:item.user="{ item }")
              div {{ fullName(item.user) }}
            template(v-slot:item.action="{ item }")
              a(
                v-if="ShowEntityEdit(entityName)",
                style="text-decoration: none",
                @click="toEdit(item)",
              )
                v-icon.primary--text(small) mdi-pencil
              router-link(
                v-if="ShowEntityView(entityName) && !ShowEntityEdit(entityName)",
                style="text-decoration: none",
                :to="getViewRoute(item)"
              )
                v-icon.primary--text(small) mdi-eye
              app-entity-duplicate(:title="item.name || entityName", :externalId="item.externalId",@confirm="duplicate(item)")
                template(v-slot:btn="{ on }")
                  v-icon(small, color="primary", v-on="on") mdi-content-duplicate
              app-entity-delete(:title="item.name || entityName", @confirm="remove(item)", v-if="ShowEntityDelete(entityName)")
                template(v-slot:btn="{ on }")
                  v-icon(small, color="red", v-on="on") mdi-delete
    v-row
      v-snackbar(v-model="snackbar")
        | {{ message }}
        v-btn(color="pink", text, @click="snackbar = false")
</template>

<style type="text/css">
.v-data-table td {
  white-space: nowrap;
}
.top-right {
  position: absolute;
  top: 1rem;
  right: .5rem;
}
</style>

<script>
import axios from 'axios'
import { mapState } from 'vuex';
import get from 'lodash/get'
import { AppEntityDelete, AppEntityDuplicate } from '@@/components'
import AppEntityAutocomplete from '@@/components/EntityAutocomplete.vue'
import FieldArrangement from "@/components/FieldArrangement";
import EntiyLoadsMixin from "@/mixins/entity-loads.mixin";
import PaginationMixin from '@/mixins/pagination-index.mixin';
import PortalMixin from '@/mixins/portal.mixin';
import IndexFilterMixin from '@/mixins/index-filter.mixin';

export default {
  components: {
    AppEntityDelete,
    AppEntityDuplicate,
    AppEntityAutocomplete,
    FieldArrangement
  },
  name: 'shipment-index',
  mixins: [
    EntiyLoadsMixin,
    PaginationMixin,
    PortalMixin,
    IndexFilterMixin,
  ],
  data () {
    return {
      message: '',
      snackbar: false,
      loading: false,
      timer: null,
      wait: 500,
      pagination: {
        where: [],
      },
      options: {},
      statusList: [],
      assignedList: [],
      dialog: false,
      headers: [],
      loadingRoute: true,
    };
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        try {
          this.loading = true;
          this.loadingRoute = true;
          this.clear();
          await this.setPortalFiltersToRoute(this.$route);
          this.load(this.options);

          if (this.portal) {
            this.setHeaders([
              { text: this.$t('Common.actions'), value: "action" },
              { text: this.$t('Shipment.externalId'), value: "externalId" },
              { text: this.$t('Shipment.origin'), value: "partyFrom.name" },
              { text: this.$t('Shipment.destination'), value: "partyTo.name" },
              { text: this.$t('Shipment.status'), value: "status.name" },
              { text: this.$t('Shipment.pickupDate'), value: "datePickupFrom", type: "DateTime" },
              { text: this.$t('Shipment.deliveryDate'), value: "dateDeliveryFrom", type: "DateTime" },
              { text: this.$t('Shipment.user'), value: "user.fullName" },
              { text: this.$t('Shipment.createdAt'), value: "createdAt", type: "DateTime" },
              { text: this.$t('Shipment.modifiedAt'), value: "modifiedAt", type: "DateTime" },
            ]);
          } else {
            this.setHeaders([]);
          }
        } catch (e) {
          console.log(this.$cleanString(e))
        } finally {
          this.loadingRoute = false;
        }
      }
    },
    options: {
      handler (options) {
        // The table was triggering this on mounted due to .sync
        // Wrong data displayed if this call was slower than the router one (this doesn't has expected criterias)
        // Fix: Disable if the pagination is being changed by the route watcher
        if (!this.loadingRoute) this.load(options)
      }
    },
    locale() {
      // Reset headers to trigger computed
      this.setHeaders([]);
    },
  },

  computed: {
    ...mapState([
      'config',
    ]),
    has () {
      return !!this.items.length
    },
    entityName () {
      return 'Shipment'
    },
    entity () {
      return this.$searchEntity(this.entityName, 'name')
    },
    columnIndexing() {
      const headersConfig = get(this.config, `index.${this.entity.name}`);

      return this.$i18n.locale && headersConfig && headersConfig.length > 0 ? headersConfig :
        this.$fieldsToHeaders(
          this.entity,
          this.$fields(this.entity, 'primitive')
        );
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  methods: {
    getViewRoute(item) {
      if (this.portal)
        return `/${this.$scope()}/${this.entityName}/portal/${this.portal}/${item.uid}/view`;
      else
        return `/${this.$scope()}/${this.entityName}/${item.uid}/view`;
    },
    toEdit(item) {
      if (this.portal)
        this.$savePaginationAndRoute(this.entityName, this.pagination, this.options, `/${this.$scope()}/${this.entityName}/portal/${this.portal}/${item.uid}/edit`);
      else
        this.$savePaginationAndRoute(this.entityName, this.pagination, this.options, `/${this.$scope()}/${this.entityName}/${item.uid}/edit`)
    },
    toCreate() {
      if (this.portal)
        this.$savePaginationAndRoute(this.entityName, this.pagination, this.options, `/${this.$scope()}/${this.entityName}/portal/${this.portal}/add`);
      else
        this.$savePaginationAndRoute(this.entityName, this.pagination, this.options, `/${this.$scope()}/${this.entityName}/add`);
    },
    setHeaders(headers) {
      this.headers = headers.length ? [...headers] : this.columnIndexing;
    },
    fullName(user) {
      return `${_.get(user, 'name', '')} ${_.get(user, 'lastName', '')}`
    },

    /* TODO: docs */
    async load (options = null) {
      clearTimeout(this.timer)
      this.timer = setTimeout(async () => {
        const pagination = {...this.getPagination(options, this.pagination.where)}
        if (this.entity) {
          this.loading = true;
          try {
            pagination.params.loadExtraData = this.$getExtraDataForIndex(this.$store, "Shipment");
            this.loads = [...new Set(this.loads)];
            const paginationresult = (await axios.get(this.$fetchLoad(this.entityName, true, ...this.loads), pagination)).data;
            paginationresult.data = (paginationresult.data || []).map(d => ({
              // The date should be a UTC format, otherwise it's not going to work.
              ...d,
            }));
            this.pagination = paginationresult;
          } catch (err) {}
          this.loading = false
        }
      }, this.wait)
    },

    /* TODO: docs */
    async remove (entity) {
      this.loading = true
      entity.entityState = ENV.ENTITY.STATE.DELETED
      try {
        await axios.post('entity/graph', entity)
      } catch (e) {
        this.snackbar = true
        this.message = e.response && e.response.data.message
      }
      await this.load(this.options)
    },

    /* Duplicate method */
    async duplicate (entity) {
      this.loading = true
      try {
        await axios.post(`Duplication/Shipment/${entity.uid}`);
      } catch (e) {
        this.snackbar = true;
        this.message = e.response && e.response.data.message;
      }
      await this.load(this.options)
    },

    clear () {
      this.options = {
        ...this.options,
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      };
      this.pagination = {};
    },

    async changeStatus(evt, item) {
      try {
        this.loading = true;
        let shipmentSaved = await axios.post(`changeStatusAtIndex/${item._Entity}/${item.uid}/setStatus/${evt}`);
        //Refresh index only if status change is successful
        if(shipmentSaved) {
          await this.load(this.options);
        }
        else {
          this.loading = false;
        }
      } catch (e) {
        console.log(e);
      }
    },
    async changeAssigned(evt, item) {
      try {
        this.loading = true;
        // TODO ENDPOINT
        let shipmentSaved = await axios.post(`assignUser/${item.uid}/${evt.externalId}`);
        //Refresh index only if status change is successful
        if(shipmentSaved) {
          await this.load(this.options);
        } else {
          this.loading = false;
        }
      } catch (e) {
        console.log(e);
      }
    },
    getSavedPagination() {
      const { pagination, options } = this.$getSavedPagination(this.entityName);
      if(pagination && options) {
        this.pagination = pagination;
        this.options = options;
        this.load(this.options);
      }
    },
  },
  async mounted() {
    this.$store.commit('SET_ERRORS', { });
    try {
      ({ data: { data: this.statusList } } = await axios.get('Status', {
        params: { pageSize: -1, where: [`x => x.entity == "Shipment"`] }
      }));
      ({ data: { data: this.assignedList } } = await axios.get('Users', {
        params: { pageSize: -1 }
      }));
    } catch (e) {
      console.log(e);
    }
    // this.headers = this.columnIndexing;
  },
  created() {
    this.getSavedPagination();
  },
}
</script>
